import axios from 'axios';
import { batch } from 'react-redux';
import toast from 'react-hot-toast';
import { store } from 'redux/store';
import { SET_COSNETICS_STATE, SET_SESSION, SET_TOKEN } from 'redux/actions/types';
import { IReducerUser } from 'redux/reducers/userReducer';
import { CosneticsAuthenticationError } from 'redux/database/cosneticsAuthentication';
import { SessionCoordinator, SessionIdentity, SessionRecord, SessionTicket, SessionUnavailableError, normalizeBaseURL } from './SessionCoordinator';
import { readSessionActivity, sessionActivityKey } from 'components/Timer/sessionActivity';

export type { SessionIdentity, SessionRecord, SessionTicket } from './SessionCoordinator';

let coordinator: SessionCoordinator | undefined;
let loginAttempt = 0;
const renewalClient = axios.create();
const unavailableToastId = 'cosnetics-session-unavailable';
let lastWarning: { reason: string; at: number } | undefined;
const accountKey = (baseURL: string, email: string) =>
  `bips:session-account:v2:${JSON.stringify([normalizeBaseURL(baseURL), email.trim().toLowerCase()])}`;

const rememberAccount = (baseURL: string, user: { Id: number; UserInstanceEmail?: string }) => {
  if (user.UserInstanceEmail) {
    window.localStorage.setItem(accountKey(baseURL, user.UserInstanceEmail), String(user.Id));
  }
};

export const currentSessionIdentity = (): SessionIdentity | null => {
  const state = store.getState();
  const baseURL = state.config.baseURL || state.config.hostname?.baseURL;
  const userId = state.user.user.Id;
  return baseURL && userId ? { baseURL, userId } : null;
};

const sameIdentity = (left: SessionIdentity | null, right: SessionIdentity) =>
  Boolean(left && String(left.userId) === String(right.userId) &&
    normalizeBaseURL(left.baseURL) === normalizeBaseURL(right.baseURL));

const applySession = (session: SessionRecord) => {
  if (!sameIdentity(currentSessionIdentity(), session)) return;
  const user = store.getState().user;
  const active = session.status === 'active';
  if (!active || session.expiresAt > Date.now()) toast.dismiss(unavailableToastId);
  if (user.auth.token !== session.token || user.cosnetics !== active) {
    console.info('[session] Session state changed', {
      status: session.status, reason: session.reason, generation: session.generation,
      expiresAt: session.expiresAt
    });
  }
  batch(() => {
    if (user.auth.token !== session.token) store.dispatch({ type: SET_TOKEN, payload: session.token });
    if (user.auth.timeout !== session.timeoutMinutes) store.dispatch({ type: SET_SESSION, payload: session.timeoutMinutes });
    if (user.cosnetics !== active) store.dispatch({ type: SET_COSNETICS_STATE, payload: active });
  });
};

/** Lazy initialization keeps transport imports safe during Redux creation. */
export const getSessionCoordinator = (): SessionCoordinator => {
  if (!coordinator) {
    let storage: Storage | undefined;
    try { storage = window.localStorage; } catch { /* The coordinator fails closed. */ }
    coordinator = new SessionCoordinator({
      storage,
      locks: typeof navigator !== 'undefined' ? navigator.locks : undefined,
      renew: async (ticket) => {
        // Renewal already owns the exclusive lock; never re-enter interception.
        const response = await renewalClient.get('AuthAPI.ashx', {
          baseURL: ticket.baseURL,
          headers: { Auth: ticket.token },
          timeout: 30_000,
          validateStatus: () => true
        });
        return { status: response.status, token: response.headers?.auth, timeout: response.headers?.timeout };
      },
      onChange: applySession
    });
  }
  return coordinator;
};

export const beginCosneticsSession = async (credentials: SessionIdentity & { token: string; timeout?: unknown }) => {
  const session = await getSessionCoordinator().start(credentials);
  applySession(session);
  return session;
};

/** Publish an authenticated session before exposing its credentials in Redux. */
export const loginCosneticsSession = async <T>(
  email: string,
  baseURL: string,
  authenticate: (isCurrent: () => boolean) => Promise<{ userId: number; token: string; timeout?: unknown; result: T }>
) => {
  const attempt = ++loginAttempt;
  const isCurrent = () => {
    const config = store.getState().config;
    const selectedAPI = config.baseURL || config.hostname?.baseURL;
    return attempt === loginAttempt && Boolean(selectedAPI) &&
      normalizeBaseURL(selectedAPI) === normalizeBaseURL(baseURL);
  };
  if (!navigator.locks) throw new SessionUnavailableError('coordination-unavailable');
  // Firebase also changes authentication state during login. Serialize complete
  // sign-ins, including fresh tabs, so an older attempt cannot finish after a
  // newer one and install the wrong Firebase identity.
  return navigator.locks.request('bips:session:login', { mode: 'exclusive' }, async () => {
    if (!isCurrent()) throw new CosneticsAuthenticationError();
    const previous = store.getState().user.user;
    let identity = currentSessionIdentity();
    if (!identity || normalizeBaseURL(identity.baseURL) !== normalizeBaseURL(baseURL) ||
        previous.UserInstanceEmail?.toLowerCase() !== email.trim().toLowerCase()) {
      try {
        const knownId = email ? window.localStorage.getItem(accountKey(baseURL, email)) : null;
        identity = knownId ? { baseURL, userId: knownId } : null;
      } catch { throw new SessionUnavailableError('coordination-unavailable'); }
    }
    let authenticated: Awaited<ReturnType<typeof authenticate>> | undefined;
    const perform = async () => {
      authenticated = await authenticate(isCurrent);
      if (!isCurrent()) throw new CosneticsAuthenticationError();
      try { rememberAccount(baseURL, { Id: authenticated.userId, UserInstanceEmail: email }); }
      catch { throw new SessionUnavailableError('coordination-unavailable'); }
      return authenticated;
    };
    let session: SessionRecord;
    if (identity) {
      const knownIdentity = identity;
      session = await getSessionCoordinator().replace(knownIdentity, async () => {
        const value = await perform();
        if (String(value.userId) !== String(knownIdentity.userId)) throw new CosneticsAuthenticationError();
        return value;
      }, isCurrent);
    } else {
      const value = await perform();
      session = await getSessionCoordinator().start({ ...value, baseURL }, isCurrent);
    }
    if (!isCurrent() || !authenticated) throw new CosneticsAuthenticationError();
    return {
      session,
      result: authenticated.result,
      isCurrent: () => isCurrent() && isCurrentCosneticsSession(session, false)
    };
  });
};

export const isCurrentCosneticsSession = (session: SessionTicket, requireUser = true): boolean => {
  try {
    if (requireUser && !sameIdentity(currentSessionIdentity(), session)) return false;
    const current = getSessionCoordinator().get(session);
    return current?.status === 'active' && current.sessionId === session.sessionId;
  } catch { return false; }
};

/** Snapshots carry profile data; the shared record owns authentication. */
export const restoreCosneticsSession = async (user: IReducerUser, baseURL: string): Promise<IReducerUser> => {
  if (!user?.user?.Id || !baseURL || !user.cosnetics || !user.auth?.token) {
    return { ...user, auth: { ...user.auth, token: '' }, cosnetics: false };
  }
  try {
    rememberAccount(baseURL, user.user);
    const session = await getSessionCoordinator().attach({
      baseURL, userId: user.user.Id, token: user.auth.token, timeout: user.auth.timeout,
      lastActivity: readSessionActivity(sessionActivityKey(baseURL, user.user.Id)) ?? undefined
    });
    return {
      ...user,
      auth: { ...user.auth, token: session?.token || '', timeout: session?.timeoutMinutes ?? user.auth.timeout },
      cosnetics: session?.status === 'active'
    };
  } catch (error) {
    reportSessionError(error);
    return { ...user, auth: { ...user.auth, token: '' }, cosnetics: false };
  }
};

export interface SessionRequestContext extends SessionIdentity {
  sessionId?: string;
  token: string;
}

export const captureCosneticsSession = (): SessionRequestContext => {
  const identity = currentSessionIdentity();
  const user = store.getState().user;
  if (!identity || !user.cosnetics || !user.auth.token) throw new CosneticsAuthenticationError();
  const current = getSessionCoordinator().get(identity);
  if (current?.status === 'signed-out') {
    applySession(current);
    throw new CosneticsAuthenticationError();
  }
  return { ...identity, sessionId: current?.sessionId, token: current?.token || user.auth.token };
};

const attachCurrentSession = async (expected: SessionRequestContext) => {
  if (!sameIdentity(currentSessionIdentity(), expected)) throw new CosneticsAuthenticationError();
  const user = store.getState().user;
  if (!user.cosnetics || !user.auth.token) throw new CosneticsAuthenticationError();
  const session = await getSessionCoordinator().attach({
    ...expected, timeout: user.auth.timeout,
    lastActivity: readSessionActivity(sessionActivityKey(expected.baseURL, Number(expected.userId))) ?? undefined
  });
  if (!session || session.status !== 'active' ||
      (expected.sessionId ? session.sessionId !== expected.sessionId : session.token !== expected.token)) {
    throw new CosneticsAuthenticationError();
  }
  return session;
};

export const withCosneticsSession = async <T>(
  request: (ticket: SessionTicket) => Promise<T>,
  expected?: SessionRequestContext
): Promise<T> => {
  let captured = expected;
  try {
    captured = expected || captureCosneticsSession();
    const session = await attachCurrentSession(captured);
    return await getSessionCoordinator().withRequest(captured, async (ticket) => {
      // Never send an action queued before logout under a subsequent login.
      if (!sameIdentity(currentSessionIdentity(), ticket) || ticket.sessionId !== session.sessionId || !store.getState().user.cosnetics) {
        throw new CosneticsAuthenticationError();
      }
      return request(ticket);
    });
  } catch (error) {
    if ((error as { code?: string })?.code === 'SESSION_UNAVAILABLE') reportSessionError(error);
    if ((error as { code?: string })?.code === 'COSNETICS_AUTHENTICATION_REQUIRED') {
      if (captured && sameIdentity(currentSessionIdentity(), captured) && !getSessionCoordinator().get(captured)) {
        batch(() => {
          store.dispatch({ type: SET_COSNETICS_STATE, payload: false });
          store.dispatch({ type: SET_TOKEN, payload: '' });
        });
      }
      throw new CosneticsAuthenticationError();
    }
    throw error;
  }
};

/*
 * Old activity timestamps are used only when adopting a pre-coordinator tab;
 * subsequent logins and renewals are entirely owned by the shared record.
 */
const attachLegacyCurrentSession = async (identity: SessionIdentity) => {
  const user = store.getState().user;
  if (!user.cosnetics || !user.auth.token) return;
  rememberAccount(identity.baseURL, user.user);
  await getSessionCoordinator().attach({
    ...identity, token: user.auth.token, timeout: user.auth.timeout,
    lastActivity: readSessionActivity(sessionActivityKey(identity.baseURL, Number(identity.userId))) ?? undefined
  });
};

export const rejectCosneticsSession = (ticket: SessionTicket): Promise<boolean> => getSessionCoordinator().reject(ticket);

export const logoutCosneticsSession = async (): Promise<void> => {
  const identity = currentSessionIdentity();
  const user = store.getState().user;
  if (identity) await getSessionCoordinator().logout(identity, undefined, { token: user.auth.token, timeout: user.auth.timeout });
};

export const sessionLogoutMiddleware = () => (next) => (action) => {
  // Capture the identity before LOG_OUT wipes Redux; the core also fences its ID.
  if (action.type === 'LOG_OUT') {
    loginAttempt += 1;
    void logoutCosneticsSession().catch(reportSessionError);
  }
  return next(action);
};

export const reportSessionError = (error: unknown) => {
  const reason = (error as { reason?: string })?.reason || 'request-failed';
  if ((error as { code?: string })?.code === 'SESSION_UNAVAILABLE') {
    toast.error((error as Error).message, { id: unavailableToastId, duration: 6000 });
  }
  if (lastWarning?.reason === reason && Date.now() - lastWarning.at < 60_000) return;
  lastWarning = { reason, at: Date.now() };
  // Axios errors can contain authorization headers. Log only sanitized metadata.
  console.warn('[session] Session check could not complete', { reason });
};

export const attachCurrentCosneticsSession = async (): Promise<void> => {
  const identity = currentSessionIdentity();
  if (identity) await attachLegacyCurrentSession(identity);
};
